import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { SupabaseClientLike } from './people';

type MfaAuthClient = SupabaseClientLike['auth'] & {
  mfa: {
    enroll: (params: { factorType: 'totp' }) => Promise<{
      data: {
        id: string;
        type?: string;
        totp?: { qr_code: string; secret: string; uri: string };
      } | null;
      error: { message?: string } | null;
    }>;
    challengeAndVerify: (params: { factorId: string; code: string }) => Promise<{
      data: unknown;
      error: { message?: string } | null;
    }>;
    listFactors: () => Promise<{
      data: { all?: unknown[]; totp?: Array<Record<string, unknown>> } | null;
      error: { message?: string } | null;
    }>;
    unenroll: (params: { factorId: string }) => Promise<{
      data: unknown;
      error: { message?: string } | null;
    }>;
  };
};

export interface MfaFactor {
  id: string;
  type: string;
  status: string;
  created_at: string | null;
}

export interface TotpEnrollment {
  id: string;
  qr_code: string;
  secret: string;
  totp_uri: string;
}

export interface SessionRow {
  id: string;
  created_at: string | null;
  updated_at: string | null;
  last_active_at: string | null;
  current: boolean;
  user_agent: string | null;
  ip: string | null;
}

export interface TenantSecurityPolicy {
  tenant_id: string;
  require_2fa_roles: string[];
  session_timeout_min: number;
  max_sessions: number;
  updated_at: string;
}

export const securityKeys = {
  all: ['security'] as const,
  factors: () => [...securityKeys.all, 'factors'] as const,
  sessions: () => [...securityKeys.all, 'sessions'] as const,
  policy: (tenantId: string) => [...securityKeys.all, 'policy', tenantId] as const,
} as const;

function resolveMfaClient(supabase: SupabaseClientLike): { auth: MfaAuthClient } {
  return supabase as SupabaseClientLike as unknown as { auth: MfaAuthClient };
}

async function getAuthHeaders(supabase: SupabaseClientLike): Promise<Record<string, string>> {
  const { data } = await supabase.auth.getSession();
  const token = data.session?.access_token;
  return token ? { authorization: `Bearer ${token}` } : {};
}

async function apiFetch<T>(
  supabase: SupabaseClientLike,
  path: string,
  init?: RequestInit,
): Promise<T> {
  const headers = await getAuthHeaders(supabase);
  const res = await fetch(`/functions/v1/${path}`, {
    ...init,
    headers: { 'content-type': 'application/json', ...headers, ...init?.headers },
  });
  if (!res.ok) {
    const body = (await res.json().catch(() => ({ error: { message: res.statusText } }))) as {
      error?: { message?: string };
    };
    throw new Error(body.error?.message ?? res.statusText);
  }
  if (res.status === 204) return {} as T;
  return res.json() as Promise<T>;
}

export async function enrollTotp(supabase: SupabaseClientLike): Promise<TotpEnrollment> {
  const client = resolveMfaClient(supabase);
  const { data, error } = await client.auth.mfa.enroll({ factorType: 'totp' });
  if (error || !data?.totp) {
    throw new Error(error?.message ?? 'Unable to enroll TOTP');
  }

  return {
    id: data.id,
    qr_code: data.totp.qr_code,
    secret: data.totp.secret,
    totp_uri: data.totp.uri,
  };
}

export async function verifyTotpEnrollment(
  supabase: SupabaseClientLike,
  factorId: string,
  code: string,
): Promise<void> {
  const client = resolveMfaClient(supabase);
  const { error } = await client.auth.mfa.challengeAndVerify({ factorId, code });
  if (error) throw new Error(error.message ?? 'Unable to verify TOTP enrollment');
}

export async function listMfaFactors(supabase: SupabaseClientLike): Promise<MfaFactor[]> {
  const client = resolveMfaClient(supabase);
  const { data, error } = await client.auth.mfa.listFactors();
  if (error) throw new Error(error.message ?? 'Unable to list MFA factors');

  const rows = data?.all ?? data?.totp ?? [];
  return rows.map((row) => {
    const factor = row as {
      id?: string;
      factor_type?: string;
      friendly_name?: string;
      status?: string;
      created_at?: string;
    };
    return {
      id: factor.id ?? '',
      type: factor.factor_type ?? factor.friendly_name ?? 'totp',
      status: factor.status ?? 'unverified',
      created_at: factor.created_at ?? null,
    };
  });
}

export async function unenrollFactor(
  supabase: SupabaseClientLike,
  factorId: string,
): Promise<void> {
  const client = resolveMfaClient(supabase);
  const { error } = await client.auth.mfa.unenroll({ factorId });
  if (error) throw new Error(error.message ?? 'Unable to remove MFA factor');
}

export async function challengeAndVerify(
  supabase: SupabaseClientLike,
  factorId: string,
  code: string,
): Promise<void> {
  return verifyTotpEnrollment(supabase, factorId, code);
}

export function useMfaFactors(supabase: SupabaseClientLike) {
  return useQuery({
    queryKey: securityKeys.factors(),
    queryFn: () => listMfaFactors(supabase),
  });
}

export function useEnrollTotp(supabase: SupabaseClientLike) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => enrollTotp(supabase),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: securityKeys.factors() });
    },
  });
}

export function useVerifyTotpEnrollment(supabase: SupabaseClientLike) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ factorId, code }: { factorId: string; code: string }) =>
      verifyTotpEnrollment(supabase, factorId, code),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: securityKeys.factors() });
    },
  });
}

export function useUnenrollFactor(supabase: SupabaseClientLike) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (factorId: string) => unenrollFactor(supabase, factorId),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: securityKeys.factors() });
    },
  });
}

export interface ActiveSessionsResponse {
  sessions: SessionRow[];
  current_session_id: string | null;
}

export function useActiveSessions(supabase: SupabaseClientLike) {
  return useQuery({
    queryKey: securityKeys.sessions(),
    queryFn: () => apiFetch<ActiveSessionsResponse>(supabase, 'security/sessions'),
  });
}

export function useTerminateSession(supabase: SupabaseClientLike) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (sessionId: string) => {
      const headers = await getAuthHeaders(supabase);
      const res = await fetch(`/functions/v1/security/sessions/${sessionId}`, {
        method: 'DELETE',
        headers,
      });
      if (!res.ok && res.status !== 204) {
        const body = (await res.json().catch(() => ({ error: { message: res.statusText } }))) as {
          error?: { message?: string };
        };
        throw new Error(body.error?.message ?? res.statusText);
      }
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: securityKeys.sessions() });
    },
  });
}

export function useTerminateAllOtherSessions(supabase: SupabaseClientLike) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      const headers = await getAuthHeaders(supabase);
      const res = await fetch('/functions/v1/security/sessions', {
        method: 'DELETE',
        headers,
      });
      if (!res.ok && res.status !== 204) {
        const body = (await res.json().catch(() => ({ error: { message: res.statusText } }))) as {
          error?: { message?: string };
        };
        throw new Error(body.error?.message ?? res.statusText);
      }
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: securityKeys.sessions() });
    },
  });
}

export function useTenantSecurityPolicy(supabase: SupabaseClientLike, tenantId: string) {
  return useQuery({
    queryKey: securityKeys.policy(tenantId),
    queryFn: () => apiFetch<TenantSecurityPolicy>(supabase, 'security/policy'),
    enabled: Boolean(tenantId),
  });
}

export function useUpdateTenantSecurityPolicy(supabase: SupabaseClientLike, tenantId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (patch: Partial<TenantSecurityPolicy>) =>
      apiFetch<TenantSecurityPolicy>(supabase, 'security/policy', {
        method: 'PATCH',
        body: JSON.stringify(patch),
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: securityKeys.policy(tenantId) });
    },
  });
}
